import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  Group,
  Text,
  TextInput,
  Stack,
  Badge,
  ActionIcon,
  Modal,
  Checkbox,
  ScrollArea,
  rem,
  Collapse,
  Tooltip,
  NumberFormatter,
  Table,
  Select,
  Indicator
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronRight,
  IconTrash,
  IconEdit,
  IconFolderPlus,
  IconSearch,
  IconFolders
} from '@tabler/icons-react';
import { CombinedPortfolioGroup, Portfolio } from '../../types';
import BigNumber from 'bignumber.js';
import TriplePieChart from './triple_pie_chart';


interface PortfolioGroup {
  id: string;
  name: string;
  portfolioIds: string[];
  createdAt: string;
}

interface PortfolioGroupsManagerProps {
  portfolios: Portfolio[];
  assetManager?: string;
  onGroupsChange?: (groups: CombinedPortfolioGroup[]) => void;
}

const PortfolioGroupsManager: React.FC<PortfolioGroupsManagerProps> = ({
  portfolios,
  assetManager = 'All',
  onGroupsChange
}) => {
  // Memoize filtered portfolios to prevent recreation on every render
  const validPortfolios = useMemo(() =>
    portfolios.filter(p => p.id),
    [portfolios]
  );

  // State for viewing groups
  const [viewGroupsModalOpen, setViewGroupsModalOpen] = useState(false);
  // State for create/edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedPortfolios, setSelectedPortfolios] = useState<string[]>([]);
  const [editingGroup, setEditingGroup] = useState<PortfolioGroup | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalAssetManager, setModalAssetManager] = useState(assetManager);
  const isInitialMount = useRef(true);

  // Load/save groups
  const [groups, setGroups] = useState<PortfolioGroup[]>([]);

  // Load groups from localStorage only once
  useEffect(() => {
    try {
      const saved = localStorage.getItem('portfolioGroups');
      if (saved) setGroups(JSON.parse(saved));
    } catch { }
  }, []);

  // Save groups to localStorage (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      try {
        localStorage.setItem('portfolioGroups', JSON.stringify(groups));
      } catch { }
    }
  }, [groups]);

  // Memoize the combined groups calculation
  const combinedGroups = useMemo((): CombinedPortfolioGroup[] => {
    return groups.map(g => {
      const ports = g.portfolioIds
        .map(id => validPortfolios.find(p => p.id === id))
        .filter((p): p is Portfolio => !!p);

      // flatten positions
      const allPos = ports.flatMap(p => p.position?.local_position ?? []).filter(p => p.ticker && p.quantity !== undefined);

      // sum all cash in all ports
      const totalCash = ports.reduce((sum, p) => sum + (p.position?.cash || 0), 0);

      // aggregate by firebaseKey
      const map = new Map<string, { ticker: string; quantity: number }>();
      allPos.forEach(pos => {
        const key = pos.firebase_key;
        const prev = map.get(key);
        if (prev) prev.quantity += pos.quantity ?? 0;
        else map.set(key, { ticker: pos.ticker!, quantity: pos.quantity ?? 0 });
      });

      return {
        portfolioIds: g.portfolioIds,
        name: g.name,
        cash: totalCash,
        localPositions: Array.from(map.entries()).map(([firebase_key, val]) => ({
          firebase_key,
          ticker: val.ticker,
          quantity: val.quantity,
        })),
      };
    });
  }, [groups, validPortfolios]);

  // Use a ref to store the previous combined groups for comparison
  const prevCombinedGroupsRef = useRef<CombinedPortfolioGroup[]>([]);

  // Only call onGroupsChange when combinedGroups actually changes
  useEffect(() => {
    if (!onGroupsChange) return;

    // Deep comparison to check if groups actually changed
    const hasChanged = JSON.stringify(prevCombinedGroupsRef.current) !== JSON.stringify(combinedGroups);

    if (hasChanged) {
      prevCombinedGroupsRef.current = combinedGroups;
      onGroupsChange(combinedGroups);
    }
  }, [combinedGroups, onGroupsChange]);

  // Unique asset managers
  const assetManagers = useMemo(() => {
    const managers = new Set<string>();
    validPortfolios.forEach(p => p.managerName && managers.add(p.managerName));
    return ['All', ...Array.from(managers).sort()];
  }, [validPortfolios]);

  // Memoize helper functions
  const getPortfolioDetails = useCallback((id: string) =>
    validPortfolios.find(p => p.id === id),
    [validPortfolios]
  );

  const getPortfolioTickers = useCallback((p: Portfolio) =>
    p.position?.local_position?.filter(x => x.ticker).map(x => x.ticker!) || [],
    []
  );

  // Memoize filtered portfolios for the modal
  const filteredPortfolios = useMemo(() => {
    return validPortfolios.filter(portfolio => {
      const q = searchQuery.toLowerCase();
      const tickers = getPortfolioTickers(portfolio);
      const matchesSearch =
        (portfolio.name_en?.toLowerCase().includes(q)) ||
        (portfolio.id?.toLowerCase().includes(q)) ||
        tickers.some(t => t.toLowerCase().includes(q));
      const matchesMgr = modalAssetManager === 'All' || portfolio.managerName === modalAssetManager;
      return matchesSearch && matchesMgr;
    });
  }, [validPortfolios, searchQuery, modalAssetManager, getPortfolioTickers]);

  // Memoize other calculated values
  const hiddenSelectedCount = useMemo(() => {
    return selectedPortfolios.filter(id => {
      const p = getPortfolioDetails(id);
      if (!p) return false;
      const q = searchQuery.toLowerCase();
      const tickers = getPortfolioTickers(p);
      const match = (p.name_en?.toLowerCase().includes(q)) ||
        (p.id?.toLowerCase().includes(q)) ||
        tickers.some(t => t.toLowerCase().includes(q));
      const mgrMatch = modalAssetManager === 'All' || p.managerName === modalAssetManager;
      return !(match && mgrMatch);
    }).length;
  }, [selectedPortfolios, getPortfolioDetails, getPortfolioTickers, searchQuery, modalAssetManager]);

  const sortedFilteredPortfolios = useMemo(() => {
    return [...filteredPortfolios].sort((a, b) =>
      (selectedPortfolios.includes(a.id!) ? -1 : 1) - (selectedPortfolios.includes(b.id!) ? -1 : 1)
    );
  }, [filteredPortfolios, selectedPortfolios]);

  const calculateGroupTotals = useCallback((groupPortfolios: Portfolio[]) => {
    const totalCash = groupPortfolios.reduce((sum, p) => sum + (p.position?.cash || 0), 0);
    const securitiesMap = new Map<string, { total: number; avgCost: number }>();

    groupPortfolios.forEach(portfolio => {
      portfolio.position?.local_position?.forEach(pos => {
        const { ticker, quantity, average_cost } = pos;
        if (!ticker || quantity == null || average_cost == null) return;

        const prev = securitiesMap.get(ticker) || { total: 0, avgCost: 0 };

        const prevTotal = new BigNumber(prev.total);
        const prevAvgCost = new BigNumber(prev.avgCost);
        const qty = new BigNumber(quantity);
        const avgCost = new BigNumber(average_cost);

        const newTotal = prevTotal.plus(qty);
        const weightedCost = prevAvgCost.times(prevTotal).plus(avgCost.times(qty));
        const newAvgCost = newTotal.isZero() ? new BigNumber(0) : weightedCost.dividedBy(newTotal);

        securitiesMap.set(ticker, {
          total: newTotal.toNumber(),
          avgCost: newAvgCost.toNumber(),
        });
      });
    });

    return { totalCash, securitiesMap };
  }, []);

  const getChartData = useCallback((groupPortfolios: Portfolio[]): Record<string, Record<string, number>> => {
    const portfoliosChartData: Record<string, number> = {};

    groupPortfolios.forEach(portfolio => {
      const marketValue = portfolio.position?.total_capital_market_value || 0;
      if (marketValue > 0) {
        portfoliosChartData[portfolio.id!] = marketValue;
      }
    });

    const securitiesChartData: Record<string, number> = {};
    const groupTotals = calculateGroupTotals(groupPortfolios);
    let totalSecValue = 0;
    const totalCash = groupTotals.totalCash || 0;
    groupTotals.securitiesMap.forEach((value, key) => {
      securitiesChartData[key] = value.total * value.avgCost;
      totalSecValue += securitiesChartData[key];
    });
    if (totalCash > 0) {
      securitiesChartData["Cash"] = totalCash;
    }

    const sectorsChartData: Record<string, number> = {};
    groupPortfolios.forEach(portfolio => {
      portfolio.position?.local_position?.forEach(pos => {
        const sector = pos.sector || 'Unknown';
        const value = pos.total_market_value || 0;
        if (value > 0) {
          sectorsChartData[sector] = (sectorsChartData[sector] || 0) + value;
        }
      });
    });

    return {
      "Portfolios Value in KWD": portfoliosChartData,
      "Securities Value in KWD": securitiesChartData,
      "Sectors Value in KWD": sectorsChartData,
    }
  }, [calculateGroupTotals]);

  const renderGroupTable = useCallback((group: PortfolioGroup) => {
    const groupPortfolios = group.portfolioIds
      .map(id => getPortfolioDetails(id))
      .filter((p): p is Portfolio => !!p);
    if (!groupPortfolios.length) return null;
    const { totalCash, securitiesMap } = calculateGroupTotals(groupPortfolios);
    const allTickers = Array.from(new Set(
      groupPortfolios.flatMap(p => p.position?.local_position?.map(x => x.ticker).filter(Boolean) || [])
    )).sort() as string[];
    return (
      <Box mt="sm">
        {allTickers.length + 2 > 10 && (
          <Text size="xs" c="yellow" mb="xs">
            ⚠️ Showing {allTickers.length} securities. Scroll horizontally to view all columns.
          </Text>
        )}
        <Box style={{ overflowX: 'auto' }}>
          <ScrollArea type="always" offsetScrollbars>
            <Table fz="xs" withTableBorder withColumnBorders style={{ minWidth: `${150 + (allTickers.length + 2) * 120}px` }}>
              <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <Table.Tr>
                  <Table.Th fw="bold" style={{ position: 'sticky', left: 0, zIndex: 11 }}>Portfolio</Table.Th>
                  <Table.Th fw="bold" colSpan={2} >Cash</Table.Th>
                  {allTickers.map(ticker => (
                    <Table.Th key={ticker} colSpan={3} style={{ minWidth: '150px' }}>
                      <Text size="xs" truncate fw="bold" style={{ maxWidth: '140px' }}>{ticker}</Text>
                    </Table.Th>
                  ))}
                </Table.Tr>
                <Table.Tr>
                  <Table.Th fw="bold" style={{ position: 'sticky', left: 0, zIndex: 11 }}></Table.Th>
                  <Table.Th fw="bold" >Qty</Table.Th>
                  <Table.Th fw="bold" >%</Table.Th>
                  {allTickers.map(ticker => (<>
                    <React.Fragment key={ticker}>
                      <Table.Th fw="bold" >Qty</Table.Th>
                      <Table.Th fw="bold" >Avg. Cost</Table.Th>
                      <Table.Th fw="bold" >%</Table.Th>
                    </React.Fragment>
                  </>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {groupPortfolios.map(p => {
                  const cash = p.position?.cash || 0;
                  return (
                    <Table.Tr key={p.id}>
                      <Table.Td style={{ position: 'sticky', left: 0, zIndex: 1 }}>
                        <Tooltip label={p.name_en}><Text size="xs" truncate>{p.id}</Text></Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">
                          <NumberFormatter value={cash} suffix=" KWD" thousandSeparator="," decimalScale={3} />
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">
                          <Text span> ({((cash / (totalCash || 1)) * 100).toFixed(1)}%)</Text>
                        </Text>
                      </Table.Td>
                      {allTickers.map(t => {
                        const pos = p.position?.local_position?.find(x => x.ticker === t);
                        const qty = pos?.quantity || 0;
                        const avgCost = pos?.average_cost || 0;
                        return (
                          <>
                            <Table.Td key={t + "1"}>
                              <Text size="xs">
                                {pos ? (
                                  <>
                                    <NumberFormatter value={qty} thousandSeparator="," />
                                  </>
                                ) : '-'}
                              </Text>
                            </Table.Td>
                            <Table.Td key={t + "2"}>
                              <Text size="xs">
                                {pos ? (
                                  <>
                                    <NumberFormatter value={avgCost} thousandSeparator="," decimalScale={5} />
                                  </>
                                ) : '-'}
                              </Text>
                            </Table.Td>
                            <Table.Td key={t + "3"}>
                              <Text size="xs">
                                {pos ? (
                                  <>
                                    <Text span > {((qty / (securitiesMap.get(t)?.total || 1)) * 100).toFixed(1)}%</Text>
                                  </>
                                ) : '-'}
                              </Text>
                            </Table.Td>
                          </>
                        );
                      })}
                    </Table.Tr>
                  );
                })}
                <Table.Tr style={{ fontWeight: 'bold', backgroundColor: 'var(--mantine-color-gray-0)' }}>
                  <Table.Td style={{ position: 'sticky', left: 0, backgroundColor: 'var(--mantine-color-gray-0)', zIndex: 1 }}>Total</Table.Td>
                  <Table.Td colSpan={2}>
                    <Text size="xs" fw={600}>
                      <NumberFormatter value={groupPortfolios.reduce((s, p) => s + (p.position?.cash || 0), 0)} suffix=" KWD" thousandSeparator="," decimalScale={3} />
                    </Text>
                  </Table.Td>
                  {Array.from(allTickers).map(t => (
                    <>
                      <Table.Td key={t + "1"} style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                        <Text size="xs" fw={600}>
                          <NumberFormatter value={securitiesMap.get(t)?.total || 0} thousandSeparator="," />
                        </Text>
                      </Table.Td>
                      <Table.Td key={t + "2"} style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                        <Text size="xs" fw={600}>
                          <NumberFormatter value={securitiesMap.get(t)?.avgCost || 0} decimalScale={5} thousandSeparator="," />
                        </Text>
                      </Table.Td>
                      <Table.Td key={t + "3"} style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                        <Text size="xs" fw={600}>
                          <Text span >100%</Text>
                        </Text>
                      </Table.Td>
                    </>
                  ))}
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Box>
      </Box>
    );
  }, [getPortfolioDetails, calculateGroupTotals]);

  // Handlers
  const toggleGroupExpansion = useCallback((id: string) =>
    setExpandedGroups(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]),
    []
  );

  const togglePortfolioSelection = useCallback((id: string) =>
    setSelectedPortfolios(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]),
    []
  );

  const openEditModal = useCallback((group: PortfolioGroup) => {
    setEditingGroup(group);
    setNewGroupName(group.name);
    setSelectedPortfolios(group.portfolioIds);
    setModalAssetManager(assetManager);
    setModalOpen(true);
  }, [assetManager]);

  const deleteGroup = useCallback((id: string) =>
    setGroups(prev => prev.filter(g => g.id !== id)),
    []
  );

  const handleSaveGroup = useCallback(() => {
    if (!newGroupName.trim() || !selectedPortfolios.length) return;
    if (editingGroup) {
      setGroups(prev => prev.map(g => g.id === editingGroup.id ? { ...g, name: newGroupName, portfolioIds: selectedPortfolios } : g));
    } else {
      setGroups(prev => [...prev, { id: Date.now().toString(), name: newGroupName, portfolioIds: selectedPortfolios, createdAt: new Date().toISOString() }]);
    }
    setNewGroupName(''); setSelectedPortfolios([]); setEditingGroup(null); setModalOpen(false);
  }, [newGroupName, selectedPortfolios, editingGroup]);

  return (
    <>
      {/* Button to open view modal */}
      <Indicator inline label={`${groups.length}`} disabled={groups.length === 0} color="blue" size={16}>
        <Tooltip label="Manage Portfolio Groups" withArrow>
          <ActionIcon variant='light'
            c={'blue'}
            onClick={() => setViewGroupsModalOpen(true)}
            size={'lg'}
          >

            <IconFolders size={16} />
          </ActionIcon>
        </Tooltip>
      </Indicator>

      {/* View Groups Modal */}
      <Modal
        opened={viewGroupsModalOpen}
        onClose={() => setViewGroupsModalOpen(false)}
        title={
          <Group p="apart" align="center">
            <Text>{`Portfolio Groups (${groups.length})`}</Text>
            <ActionIcon variant="subtle" size="sm" onClick={() => setModalOpen(true)}>
              <IconFolderPlus size={16} />
            </ActionIcon>
          </Group>
        }
        size="70%"
      >
        <Box>
          {groups.length > 0 ? (
            <Stack gap="xs">
              {groups.map(group => {
                const isExpanded = expandedGroups.includes(group.id);
                const groupPortfolios = group.portfolioIds
                  .map(id => getPortfolioDetails(id))
                  .filter((p): p is Portfolio => !!p);
                const { totalCash } = calculateGroupTotals(groupPortfolios);
                const data = getChartData(groupPortfolios);
                return (
                  <Card key={group.id} p="xs" withBorder>
                    <Group>
                      <ActionIcon variant="subtle" size="sm" onClick={() => toggleGroupExpansion(group.id)}>
                        {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                      </ActionIcon>
                      <Text size="sm" fw={500}>{group.name}</Text>
                      <Badge size="xs" variant="light">{groupPortfolios.length} portfolios</Badge>
                      <Text size="xs" c="dimmed">
                        <NumberFormatter value={totalCash} suffix=" KWD" thousandSeparator="," decimalScale={3} />
                      </Text>
                      <Box style={{ marginLeft: 'auto' }}>
                        <Group gap={4}>
                          <ActionIcon size="sm" variant="subtle" onClick={() => openEditModal(group)}>
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon size="sm" variant="subtle" color="red" onClick={() => deleteGroup(group.id)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Box>
                    </Group>
                    <Collapse expanded={isExpanded}>
                      <>
                        <div style={{ width: '100%', height: '450px' }}>
                          <TriplePieChart data={data} />
                        </div>
                        {renderGroupTable(group)}
                      </>
                    </Collapse>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No portfolio groups created yet. Click + to create one.
            </Text>
          )}
        </Box>
      </Modal>

      {/* Create/Edit Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingGroup(null);
          setNewGroupName('');
          setSelectedPortfolios([]);
          setSearchQuery('');
          setModalAssetManager(assetManager);
        }}
        title={editingGroup ? "Edit Portfolio Group" : "Create Portfolio Group"}
        size="lg"
      >
        <Stack>
          <TextInput
            label="Group Name"
            placeholder="Enter group name"
            value={newGroupName}
            onChange={e => setNewGroupName(e.currentTarget.value)}
            required
          />
          <Box>
            <Group justify="space-between" align="center" mb="xs">
              <Text size="sm" fw={500}>Select Portfolios</Text>
              {selectedPortfolios.length > 0 && (
                <Badge variant="filled" size="sm">
                  {selectedPortfolios.length} selected{hiddenSelectedCount > 0 && ` (${hiddenSelectedCount} hidden)`}
                </Badge>
              )}
            </Group>
            <Group grow mb="sm">
              <Select
                label="Asset Manager"
                value={modalAssetManager}
                onChange={v => setModalAssetManager(v || 'All')}
                data={assetManagers}
                size="sm"
              />
              <TextInput
                label="Search"
                placeholder="Search by name, ID, or ticker"
                leftSection={<IconSearch size={16} />}
                value={searchQuery}
                onChange={e => setSearchQuery(e.currentTarget.value)}
                size="sm"
              />
            </Group>
            <ScrollArea h={350}>
              <Stack gap="xs">
                {sortedFilteredPortfolios.length > 0 ? (
                  sortedFilteredPortfolios.map(portfolio => {
                    const isSelected = selectedPortfolios.includes(portfolio.id!);
                    const tickers = getPortfolioTickers(portfolio);
                    return (
                      <Card key={portfolio.id} p="sm" withBorder style={{ borderColor: isSelected ? 'var(--mantine-color-blue-6)' : undefined, borderWidth: isSelected ? 2 : 1 }}>
                        <Group justify="space-between">
                          <Group align="flex-start" style={{ flex: 1 }}>
                            <Checkbox checked={isSelected} onChange={() => togglePortfolioSelection(portfolio.id!)} mt={2} />
                            <Box style={{ flex: 1 }}>
                              <Text size="sm" fw={500} lineClamp={1}>{portfolio.name_en}</Text>
                              <Text size="xs" c="dimmed">ID: {portfolio.id}</Text>
                              {portfolio.managerName && <Text size="xs" c="dimmed" lineClamp={1}>Manager: {portfolio.managerName}</Text>}
                              {tickers.length > 0 && <Text size="xs" c="dimmed" mt={4} lineClamp={2}>Tickers: {tickers.join(', ')}</Text>}
                            </Box>
                          </Group>
                          <Box style={{ minWidth: '120px', textAlign: 'right' }}>
                            {portfolio.position?.cash !== undefined && (
                              <>
                                <Text size="xs" c="dimmed">Cash</Text>
                                <Text size="sm" fw={500}>
                                  <NumberFormatter value={portfolio.position.cash} suffix=" KWD" thousandSeparator="," decimalScale={3} />
                                </Text>
                              </>
                            )}
                            {tickers.length > 0 && <Text size="xs" c="dimmed" mt={4}>{tickers.length} securities</Text>}
                          </Box>
                        </Group>
                      </Card>
                    );
                  })
                ) : (
                  <Text size="sm" c="dimmed" ta="center">
                    No portfolios found matching your criteria
                    {hiddenSelectedCount > 0 && <Text size="xs" c="dimmed" mt={4}>({hiddenSelectedCount} selected portfolios are hidden by current filters)</Text>}
                  </Text>
                )}
              </Stack>
            </ScrollArea>
          </Box>
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveGroup} disabled={!newGroupName.trim() || selectedPortfolios.length === 0}>
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default PortfolioGroupsManager;
